import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Navbar from "../Navbar";
import SelectSearchInput from "../SelectSearch";
import { DownloadTableExcel } from "react-export-table-to-excel";
import Footer from "../Footer";
import { toast } from "react-toastify";
import Select from 'react-select'
import { FaEdit, FaSave, FaTimesCircle, FaTrashAlt } from "react-icons/fa";
import StatementModal from "./StatementModal";

const Statement = () => {
  const [data, setData] = useState([]);
  const [date, setDate] = useState("");
  const [token, setToken] = useState("");
  const [outletSelected, setOutletSelected] = useState("");
  const [company, setCompany] = useState("");
  const [error, setErrorMsg] = useState("");
  const tableRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    GroupName: '',
    ItemName: '',
    BrandName: '',
    // vendor_name: '',
    UOM: '',
    CurrentLevel: 0,
    Rate: '',
    Total: 0,
    Type: 'Food',
    itemUnit: 0,
    itemUom: ''
  });

  const uomOptions = ['kg', 'gm', 'ltr', 'ml', 'pcs', 'box', 'packet', 'unit', 'bottle'];
  const typeOptions = ['Food', 'Beverage', 'Others'];

  const itemOptions = useMemo(() => {
    return data.flatMap(group =>
      group.stocks.map(item => ({
        value: item.ItemName,
        label: `${item.ItemName} (${group.group})`,
        group: group.group,
        item
      }))
    );
  }, [data]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data
      .map(group => ({
        ...group,
        stocks: group.stocks.filter(item =>
          item.ItemName.toLowerCase() === searchTerm.toLowerCase()
        )
      }))
      .filter(group => group.stocks.length > 0);
  }, [data, searchTerm]);

  const changeOutlet = (e, companyName) => {
    setOutletSelected(e);
    setCompany(companyName);
  };

  const Url = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    setToken(sessionStorage.getItem("token"));
  }, []);

  const fetchData = async () => {
    setErrorMsg("");
    try {
      const response = await axios.post(`${Url}/getstocksbygroup`, {
        token: token,
        outlet_name: outletSelected,
      });
      const dataWithEditing = response.data.map(group => ({
        ...group,
        stocks: group.stocks.map(item => ({
          ...item,
          isEditing: false,
          editValues: {
            GroupName: group.group,
            BrandName: item.BrandName,
            UOM: item.UOM,
            CurrentLevel: item.CurrentLevel,
            Rate: item.Rate,
            Total: item.Total,
            Type: item.Type || 'Food'
          }
        }))
      }));
      setData(dataWithEditing);

      const res = await axios.post(`${Url}/getlatestsyncdate`, {
        token: token,
        outlet_name: outletSelected,
      });
      setDate(res.data.last_synced);
    } catch (error) {
      console.error("Error fetching data", error);
      if (error.response && error.response.data) {
        setErrorMsg(
          error.response.data.message || "No stock records found."
        );
      } else {
        setErrorMsg("An Error Occurred.");
      }
    }
  };

  useEffect(() => {
    if (token && outletSelected) {
      fetchData();
    }
  }, [token, outletSelected]);

  const handleModalClose = () => setShowModal(false);
  const handleModalShow = () => setShowModal(true);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'CurrentLevel' || name === 'Rate') {
      setFormData(prev => {
        const currentLevel = parseFloat(name === 'CurrentLevel' ? value : prev.CurrentLevel) || 0;
        const rate = parseFloat(name === 'Rate' ? value : prev.Rate) || 0;
        return {
          ...prev,
          Total: (currentLevel * rate).toFixed(2)
        };
      });
    }
  };

  const handleEditItem = (groupIndex, itemIndex) => {
    const newData = [...data];
    newData[groupIndex].stocks[itemIndex].isEditing = true;
    setData(newData);
  };

  const handleCancelEdit = (groupIndex, itemIndex) => {
    const newData = [...data];
    newData[groupIndex].stocks[itemIndex].isEditing = false;
    setData(newData);
  };

  const handleEditChange = (groupIndex, itemIndex, e) => {
    const { name, value } = e.target;
    const newData = [...data];

    newData[groupIndex].stocks[itemIndex].editValues[name] = value;

    if (name === 'CurrentLevel' || name === 'Rate') {
      const currentLevel = parseFloat(name === 'CurrentLevel' ? value : newData[groupIndex].stocks[itemIndex].editValues.CurrentLevel) || 0;
      const rate = parseFloat(name === 'Rate' ? value : newData[groupIndex].stocks[itemIndex].editValues.Rate) || 0;
      newData[groupIndex].stocks[itemIndex].editValues.Total = (currentLevel * rate).toFixed(2);
    }

    setData(newData);
  };

  const handleSaveEdit = async (groupIndex, itemIndex) => {
    toast.info(
      <div className="p-2">
        <p className="mb-3 text-white">Are you sure you want to save these changes?</p>
        <div className="flex justify-between gap-2">
          <button
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            onClick={async () => {
              toast.dismiss();
              const item = data[groupIndex].stocks[itemIndex];
              const updatedItem = {
                ...item,
                ...item.editValues,
              };

              try {
                await axios.post(`${Url}/updatestocks`, {
                  token: token,
                  outlet_name: outletSelected,
                  stocks: [updatedItem]
                });
                toast.success('Item updated successfully');
                fetchData();
              } catch (error) {
                console.error('Error updating item:', error);
                toast.error('Failed to update item');
              }
            }}
          >
            Save
          </button>
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
        toastId: 'save-update-stock'
      }
    );
  };

  const handleDeleteItem = async (groupIndex, itemIndex) => {
    toast.info(
      <div className="p-2">
        <p className="mb-3 text-white">Are you sure you want to delete this stock?</p>
        <div className="flex justify-between gap-2">
          <button
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
            onClick={async () => {
              toast.dismiss();
              const item = data[groupIndex].stocks[itemIndex];
              try {
                await axios.post(`${Url}/deletestocks`, {
                  token: token,
                  outlet_name: outletSelected,
                  items: [
                    item.ItemName
                  ]
                });
                toast.success('Item deleted successfully');
                fetchData();
              } catch (error) {
                console.error('Error deleting item:', error);
                const errorMessage =
                  error?.response?.data?.not_deleted?.join(', ') ||
                  error?.response?.data?.error ||
                  error?.error ||
                  "Failed to delete item";
                toast.error(errorMessage);
              }
            }}
          >
            Delete
          </button>
          <button
            className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            onClick={() => toast.dismiss()}
          >
            Cancel
          </button>
        </div>
      </div>,
      {
        autoClose: false,
        closeButton: false,
        toastId: 'save-update-stock'
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${Url}/stock-statement`, {
        token: token,
        items: [{
          ...formData,
          OutletName: outletSelected
        }]
      });
      toast.success('Stock statement successfully created');

      if (formData.itemUnit && formData.itemUom) {
        try {
          const payload = {
            token: token,
            outlet: outletSelected,
            items: [{
              name: formData.ItemName,
              unit: parseFloat(formData.itemUnit),
              uom: formData.itemUom,
              rate: parseFloat(formData.Rate)
            }]
          };

          await axios.post(`${Url}/unit-definitions`, payload);
          toast.success('Unit definition successfully created');
        } catch (error) {
          console.error('Error saving unit definitions:', error);
          toast.error('Failed to save unit definitions');
        }
      }

      handleModalClose();
      fetchData();

      setFormData({
        GroupName: '',
        ItemName: '',
        BrandName: '',
        UOM: '',
        CurrentLevel: 0,
        Rate: '',
        Total: 0,
        Type: 'Food',
        itemUnit: 0,
        itemUom: ''
      })
    } catch (err) {
      toast.error('Failed to create stock statement');
      console.log(err.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-grow">
        <div className="font-sans mx-auto max-w-6xl my-5">
          <div className="flex justify-center text-center mb-3">
            <SelectSearchInput
              token={token}
              setToken={setToken}
              selectedOutlet={outletSelected}
              setSelectedOutlet={changeOutlet}
              setCompany={setCompany}
            />
          </div>

          <div className="flex justify-between items-center my-7">
            <DownloadTableExcel
              filename="statement"
              sheet="Outlet"
              currentTableRef={tableRef.current}>
              <button className="export">
                Export
              </button>
            </DownloadTableExcel>

            <div className="w-80">
              <Select
                options={itemOptions}
                placeholder="Search items..."
                isClearable
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    setSearchTerm(selectedOption.value);
                  } else {
                    setSearchTerm("");
                  }
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    boxShadow: 'none',
                    '&:hover': {
                      borderColor: '#cbd5e0',
                    },
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? '#4299e1' : state.isFocused ? '#ebf8ff' : 'white',
                    color: state.isSelected ? 'white' : 'black',
                  }),
                }}
              />
            </div>

            <button
              onClick={handleModalShow}
              className="border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg flex items-center justify-center px-3 py-2 text-sm"
            >
              Add Stock
            </button>
          </div>

          {error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : (
            <div ref={tableRef}>
              <table className="border-collapse mt-4">
                <thead>
                  <tr>
                    <th colSpan={8}>
                      <div className="flex flex-col justify-end">
                        <h1 className="text-3xl text-center underline">
                          {company}
                        </h1>

                        {decodeURIComponent(outletSelected) ? (
                          <h1 className="text-lg text-center font-medium font-poppins md:text-xl">
                            {decodeURIComponent(outletSelected || "") || ""}
                          </h1>
                        ) : (
                          <h1 className="text-lg text-center font-medium font-poppins md:text-xl">
                            {decodeURIComponent(outletSelected || "") || ""}
                          </h1>
                        )}

                        <p className="text-center underline">
                          Stock Statement Report For: {date}
                        </p>
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <th className="p-1 text-center font-bold underline text-lg">Group Name</th>
                    <th className="p-1 text-center font-bold underline text-lg">Item Name</th>
                    <th className="p-1 text-center font-bold underline text-lg">Brand Name</th>
                    <th className="p-1 text-center font-bold underline text-lg">UOM</th>
                    <th className="p-1 text-center font-bold underline text-lg">Current Level</th>
                    <th className="p-1 text-center font-bold underline text-lg">Rate</th>
                    <th className="p-1 text-center font-bold underline text-lg">Total</th>
                    <th className="p-1 text-center font-bold underline text-lg">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((group, groupIndex) => (
                    <React.Fragment key={groupIndex}>
                      <tr>
                        <td
                          className="font-bold capitalize p-2 border-b-2 border-black text-center text-base"
                          colSpan="1">
                          {group.group}
                        </td>
                      </tr>
                      {group.stocks.map((item, itemIndex) => (
                        <tr key={itemIndex}>
                          <td className="p-1 text-center"></td>
                          <td className="p-1 text-center text-sm">{item.ItemName}</td>
                          <td className="p-1 text-center">
                            {item.isEditing ? (
                              <input
                                type="text"
                                name="BrandName"
                                value={item.editValues.BrandName}
                                onChange={(e) => handleEditChange(groupIndex, itemIndex, e)}
                                className="w-32 text-center px-2 py-1 border rounded"
                              />
                            ) : (
                              <span className="text-sm text-black">{item.BrandName}</span>
                            )}
                          </td>
                          <td className="p-1 text-center">
                            {item.isEditing ? (
                              <input
                                type="text"
                                name="UOM"
                                value={item.editValues.UOM}
                                onChange={(e) => handleEditChange(groupIndex, itemIndex, e)}
                                className="w-32 text-center px-2 py-1 border rounded"
                              />
                            ) : (
                              <span className="text-sm text-black">{item.UOM}</span>
                            )}
                          </td>
                          <td className="p-1 text-center">
                            {item.isEditing ? (
                              <input
                                type="number"
                                name="CurrentLevel"
                                value={item.editValues.CurrentLevel}
                                onChange={(e) => handleEditChange(groupIndex, itemIndex, e)}
                                min="0"
                                step="0.01"
                                className="w-32 text-center px-2 py-1 border rounded"
                              />
                            ) : (
                              <span className="text-sm text-black">{item.CurrentLevel}</span>
                            )}
                          </td>
                          <td className="p-1 text-center">
                            {item.isEditing ? (
                              <input
                                type="number"
                                name="Rate"
                                value={item.editValues.Rate}
                                onChange={(e) => handleEditChange(groupIndex, itemIndex, e)}
                                min="0"
                                step="0.01"
                                className="w-32 text-center px-2 py-1 border rounded"
                              />
                            ) : (
                              <span className="text-sm text-black">{item.Rate}</span>
                            )}
                          </td>
                          <td className="p-1 text-center">
                            <span className="text-sm text-black">
                              {item.isEditing ? item.editValues.Total : item.Total}
                            </span>
                          </td>
                          <td className="p-1 text-center text-sm">
                            {item.isEditing ? (
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() => handleSaveEdit(groupIndex, itemIndex)}
                                  className="h-8 w-8 p-0 border rounded flex items-center justify-center text-green-600 hover:bg-green-50"
                                >
                                  <FaSave className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleCancelEdit(groupIndex, itemIndex)}
                                  className="h-8 w-8 p-0 border rounded flex items-center justify-center text-red-600 hover:bg-red-50"
                                >
                                  <FaTimesCircle className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex justify-center space-x-2">
                                <button
                                  onClick={() => handleEditItem(groupIndex, itemIndex)}
                                  className="h-8 w-8 p-0 border rounded flex items-center justify-center text-blue-600 hover:bg-blue-50"
                                >
                                  <FaEdit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteItem(groupIndex, itemIndex)}
                                  className="h-8 w-8 p-0 border rounded flex items-center justify-center text-red-600 hover:bg-red-50"
                                >
                                  <FaTrashAlt className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}

                      {!searchTerm && (
                        <tr>
                          <td colSpan="6" className="p-1 text-center"></td>
                          <td className="p-1 text-center text-sm font-bold border-t border-black">
                            {group.group_total.toFixed(2)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}

                  <tr>
                    <td colSpan={7} className="p-2"></td>
                  </tr>

                  {!searchTerm && (
                    <tr>
                      <td colSpan="6" className="p-1 text-center"></td>
                      <td className="p-1 text-center text-sm font-bold border-t-2 border-black">
                        {data
                          .reduce((acc, group) => acc + group.group_total, 0)
                          .toFixed(2)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <StatementModal
          showModal={showModal}
          handleModalClose={handleModalClose}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
          formData={formData}
          uomOptions={uomOptions}
          typeOptions={typeOptions}
          loading={loading}
        />
      </div>

      <Footer />
    </div>
  );
};

export default Statement;